import { useState, useCallback, useMemo, useRef, useEffect, type Dispatch, type SetStateAction } from 'react'
import { Link } from '@tanstack/react-router'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import apiClient from '@/api/client'
import type { StockCategory, StockItem, StockMovement, StockAlert, UserBrief, AdvancedStockReport } from '@/types'
import { useCurrency } from '@/contexts/CurrencyContext'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { DatePicker } from '@/components/ui/date-picker'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  Tooltip as UITooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Package, AlertTriangle, TrendingUp, Plus, Search, ArrowDownCircle, ArrowUpCircle,
  Boxes, BarChart3, ShoppingCart, Tag, X, ChevronLeft, ChevronRight, CheckCircle2, XCircle,
  MoreVertical, Pencil, DollarSign, Recycle, RefreshCw, SlidersHorizontal, FileSpreadsheet, ChevronDown,
  ArrowUpDown, ArrowUp, ArrowDown, GripVertical, Undo2, RotateCcw,
  Eye, CalendarClock, Percent, AlertCircle, Download, ClipboardCheck,
} from 'lucide-react'
import {
  PieChart, Pie, Cell, Tooltip as ReTooltip, Legend, ResponsiveContainer,
  LineChart, Line, XAxis, YAxis, CartesianGrid,
} from 'recharts'
import { InventoryPurchasingTab } from '@/components/store/InventoryPurchasingTab'
import { VendorAPTab } from '@/components/store/VendorAPTab'
import { VendorReportsTab } from '@/components/store/VendorReportsTab'
import { DateRangeFilter } from '@/components/admin/reports/DateRangeFilter'
import { useReportRange } from '@/hooks/useReportRange'
import { formatDateTimeHuman } from '@/lib/utils'
import * as ReportHelp from '@/lib/inventory-report-help'
import { PaginationControlsComponent } from '@/components/ui/pagination-controls'
import type { PaginationControls } from '@/hooks/usePagination'
import { UNIT_OPTIONS, type UnitMode, convertUnits, getCompatibleUnits } from '@/lib/inventory-units'
import { UnitSelector, FormField } from '@/components/store/inventory-form-ui'
import { compactCurrency } from '@/components/admin/dashboard/dashboardFormat'

// The stored `value` is what lands in stock_movements.note, and the reports
// grep it verbatim (`[Spoilage/Waste]` drives the shrinkage KPI in stock.go and
// inventory_reports_export.go). Change a label freely; changing a value breaks
// the reports silently.
const ISSUE_REASONS = [
  { value: 'Spoilage/Waste', label: 'Damaged / expired' },
  { value: 'Return to Vendor', label: 'Return to vendor' },
  { value: 'Staff Use', label: 'Staff use' },
  { value: 'Internal use', label: 'Internal / shop use' },
  { value: 'Sample/Demo', label: 'Sample or display' },
  { value: 'Theft/Loss', label: 'Theft / loss' },
  { value: 'Other', label: 'Other' },
] as const

const ADJUST_REASONS = [
  { value: 'Physical count correction', label: 'Physical count correction' },
  { value: 'Damage / loss', label: 'Damage / loss' },
  { value: 'System correction', label: 'System correction' },
  { value: 'Other', label: 'Other' },
] as const

type Toast = { id: number; type: 'success' | 'error'; message: string }
let toastIdCounter = 0

type Tab = 'items' | 'alerts' | 'movements' | 'reports' | 'purchasing' | 'vendors' | 'vendor-reports'
type ModalState =
  | { kind: 'none' }
  | { kind: 'editItem'; item: StockItem }
  | { kind: 'purchase'; item: StockItem }
  | { kind: 'issue'; item: StockItem }
  | { kind: 'supplierReturn'; item: StockItem }
  | { kind: 'adjust'; item: StockItem }
  | { kind: 'count'; item: StockItem }

type StockStatusFilter = '' | 'low' | 'ok'

export type ItemSortKey = 'category' | 'name' | 'on_hand' | 'reorder' | 'unit_cost' | 'expiry'
export type ItemsDataColumnId = ItemSortKey

const ITEM_COL_ORDER_STORAGE_KEY = 'pos_inventory_items_col_order_v1'
const DEFAULT_ITEM_COL_ORDER: ItemsDataColumnId[] = ['name', 'category', 'on_hand', 'reorder', 'unit_cost', 'expiry']
const ITEM_COL_LABELS: Record<ItemsDataColumnId, string> = {
  name: 'Item',
  category: 'Category',
  on_hand: 'On Hand',
  reorder: 'Reorder Lvl',
  unit_cost: 'Unit Cost',
  expiry: 'Next expiry',
}
const ITEM_COL_ALIGN: Record<ItemsDataColumnId, 'left' | 'right'> = {
  name: 'left',
  category: 'left',
  on_hand: 'right',
  reorder: 'right',
  unit_cost: 'right',
  expiry: 'left',
}
const INV_COL_DND = 'application/x-pos-inv-col'

function parseStoredItemColOrder(raw: string | null): ItemsDataColumnId[] | null {
  if (!raw) return null
  try {
    const p = JSON.parse(raw) as unknown
    if (!Array.isArray(p) || p.length !== DEFAULT_ITEM_COL_ORDER.length) return null
    const set = new Set(DEFAULT_ITEM_COL_ORDER)
    for (const x of p) {
      if (typeof x !== 'string' || !set.has(x as ItemsDataColumnId)) return null
    }
    return p as ItemsDataColumnId[]
  } catch {
    return null
  }
}

export function StoreInventoryDashboard() {
  const qc = useQueryClient()
  const { formatCurrency } = useCurrency()
  const [tab, setTab] = useState<Tab>('items')
  const [modal, setModal] = useState<ModalState>({ kind: 'none' })
  const [search, setSearch] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')
  const [filterCategory, setFilterCategory] = useState('')
  const [filterStatus, setFilterStatus] = useState<StockStatusFilter>('')
  const [itemPage, setItemPage] = useState(1)
  const [itemPageSize, setItemPageSize] = useState(10)
  const [movPage, setMovPage] = useState(1)
  const [movType, setMovType] = useState('')
  const [movFrom, setMovFrom] = useState('')
  const [movTo, setMovTo] = useState('')
  const [movCategory, setMovCategory] = useState('')
  const [toasts, setToasts] = useState<Toast[]>([])
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())
  const [itemSortKey, setItemSortKey] = useState<ItemSortKey>('category')
  const [itemSortDir, setItemSortDir] = useState<'asc' | 'desc'>('asc')

  const showToast = useCallback((type: 'success' | 'error', message: string) => {
    const id = ++toastIdCounter
    setToasts(prev => [...prev, { id, type, message }])
    setTimeout(() => setToasts(prev => prev.filter(t => t.id !== id)), 4000)
  }, [])

  useEffect(() => {
    const t = window.setTimeout(() => setDebouncedSearch(search.trim()), 300)
    return () => window.clearTimeout(t)
  }, [search])

  // Retail has ONE taxonomy: the product category. A stock item's category is
  // its product's, so the filters here are the same list the catalogue uses.
  const { data: categoriesRes } = useQuery({
    queryKey: ['categories'],
    queryFn: () => apiClient.getCategories(),
  })
  const categories = (categoriesRes?.data ?? []) as unknown as StockCategory[]

  const { data: summaryRes, isError: summaryError } = useQuery({
    queryKey: ['stockSummary'],
    queryFn: () => apiClient.getStockSummary('month'),
    staleTime: 30_000,
  })
  const stockOverview = summaryRes?.data?.overview

  const stockHealthParam: 'low' | 'ok' | undefined =
    filterStatus === 'low' ? 'low' : filterStatus === 'ok' ? 'ok' : undefined

  const handleItemSort = useCallback((key: ItemSortKey) => {
    setItemPage(1)
    setItemSortKey((prevKey) => {
      if (prevKey === key) {
        setItemSortDir((d) => (d === 'asc' ? 'desc' : 'asc'))
        return prevKey
      }
      setItemSortDir('asc')
      return key
    })
  }, [])

  const { data: itemsRes, isLoading: itemsLoading } = useQuery({
    queryKey: ['stockItems', itemPage, itemPageSize, filterCategory, debouncedSearch, filterStatus, itemSortKey, itemSortDir],
    queryFn: () =>
      apiClient.getStockItems({
        page: itemPage,
        per_page: itemPageSize,
        category_id: filterCategory || undefined,
        search: debouncedSearch || undefined,
        stock_health: stockHealthParam,
        sort: itemSortKey,
        sort_dir: itemSortDir,
      }),
  })
  const items = itemsRes?.data ?? []
  const itemsMeta = itemsRes?.meta
  const itemsTotal = itemsMeta?.total ?? 0

  const itemsPagination = useMemo((): PaginationControls => {
    const totalPages = Math.max(1, Math.ceil(itemsTotal / itemPageSize))
    return {
      page: itemPage,
      pageSize: itemPageSize,
      totalPages,
      hasNextPage: itemPage < totalPages,
      hasPreviousPage: itemPage > 1,
      goToPage: (p: number) => setItemPage(Math.max(1, Math.min(p, totalPages))),
      goToNextPage: () => setItemPage((p) => Math.min(p + 1, totalPages)),
      goToPreviousPage: () => setItemPage((p) => Math.max(1, p - 1)),
      goToFirstPage: () => setItemPage(1),
      goToLastPage: () => setItemPage(totalPages),
      setPageSize: (s: number) => {
        setItemPageSize(s)
        setItemPage(1)
      },
    }
  }, [itemPage, itemPageSize, itemsTotal])

  const { data: alertsRes } = useQuery({ queryKey: ['stockAlerts'], queryFn: () => apiClient.getStockAlerts() })
  const alerts: StockAlert[] = alertsRes?.data ?? []

  const { data: lowStockItemsForAlertsRes } = useQuery({
    queryKey: ['stockItems', 'low-for-alerts'],
    queryFn: () =>
      apiClient.getStockItems({ page: 1, per_page: 500, stock_health: 'low', sort: 'name', sort_dir: 'asc' }),
    enabled: tab === 'alerts',
  })
  const lowStockItemsForAlerts: StockItem[] = lowStockItemsForAlertsRes?.data ?? []

  const { data: catalogPickRes } = useQuery({
    queryKey: ['stockItems', 'catalog-pick'],
    queryFn: () => apiClient.getStockItems({ page: 1, per_page: 500, sort: 'name', sort_dir: 'asc' }),
    enabled: tab === 'purchasing',
  })
  const catalogPickItems: StockItem[] = catalogPickRes?.data ?? []

  const { data: usersRes } = useQuery({ queryKey: ['storeUsers'], queryFn: () => apiClient.getStoreUsers() })
  const users: UserBrief[] = usersRes?.data ?? []

  const { data: movRes } = useQuery({
    queryKey: ['stockMovements', movPage, movType, movFrom, movTo, movCategory],
    queryFn: () =>
      apiClient.getStockMovements({
        page: movPage,
        per_page: 20,
        type: movType || undefined,
        from: movFrom || undefined,
        to: movTo || undefined,
        category_id: movCategory || undefined,
      }),
    enabled: tab === 'movements',
  })
  const movements: StockMovement[] = movRes?.data ?? []
  const movMeta = movRes?.meta

  const [reportPeriod, setReportPeriod] = useState('30')
  const { data: advancedRes, isLoading: reportLoading, isError: reportError } = useQuery({
    queryKey: ['advancedStockReport', reportPeriod],
    queryFn: () => apiClient.getAdvancedStockReport(reportPeriod),
    enabled: tab === 'reports',
  })
  const advancedReport: AdvancedStockReport | undefined = advancedRes?.data

  // D-P3: surface query failures instead of silently rendering "—" KPIs / an
  // empty report. A load failure now toasts so staff know the numbers are
  // stale, not zero.
  useEffect(() => {
    if (summaryError) showToast('error', 'Could not load the inventory summary — check your connection and refresh.')
  }, [summaryError, showToast])
  useEffect(() => {
    if (reportError) showToast('error', 'Could not load the inventory report — check your connection and refresh.')
  }, [reportError, showToast])

  // #6: sale-time stock deduction failures (dead-letter). Surfaced as a banner so
  // a manager reconciles the drift instead of it vanishing into a log line.
  const { data: deductionFailuresRes } = useQuery({
    queryKey: ['stockDeductionFailures'],
    queryFn: () => apiClient.getStockDeductionFailures(),
    refetchInterval: 60_000,
  })
  const deductionFailures = deductionFailuresRes?.data ?? []
  const resolveFailure = useMutation({
    mutationFn: (id: string) => apiClient.resolveStockDeductionFailure(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['stockDeductionFailures'] })
      showToast('success', 'Marked as reconciled')
    },
    onError: (e: Error) => showToast('error', e.message || 'Failed to dismiss'),
  })

  const tabs: { id: Tab; label: string; icon: React.ReactNode; badge?: number }[] = [
    { id: 'items', label: 'Inventory', icon: <Package className="w-4 h-4" /> },
      { id: 'purchasing', label: 'Purchasing', icon: <ShoppingCart className="w-4 h-4" /> },
    { id: 'vendors', label: 'Vendors / AP', icon: <DollarSign className="w-4 h-4" /> },
    { id: 'alerts', label: 'Alerts', icon: <AlertTriangle className="w-4 h-4" />, badge: alerts.length },
    { id: 'movements', label: 'Movements', icon: <Boxes className="w-4 h-4" /> },
    { id: 'reports', label: 'Reports', icon: <BarChart3 className="w-4 h-4" /> },
    { id: 'vendor-reports', label: 'Vendor Reports', icon: <FileSpreadsheet className="w-4 h-4" /> },
  ]

  return (
    <div className="p-6 space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Inventory</h1>
          <p className="text-muted-foreground">
            Stock on hand for every product, plus purchasing and vendors
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          {/* Stock items are products now, so they are created there — one
              screen owns the catalogue and its stock record together. */}
          <Button asChild size="sm">
            <Link to="/admin/catalog">
              <Plus className="w-4 h-4 mr-1" /> Add product
            </Link>
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-4 pb-3 px-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-blue-50 shrink-0"><Package className="w-5 h-5 text-blue-600" /></div>
              <div className="min-w-0">
                <p className="truncate text-xl font-bold sm:text-2xl">
                  {stockOverview?.total_items != null ? stockOverview.total_items : (itemsMeta?.total ?? '—')}
                </p>
                <p className="text-xs text-muted-foreground">Total Items</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 pb-3 px-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-red-50 shrink-0"><AlertTriangle className="w-5 h-5 text-red-600" /></div>
              <div className="min-w-0">
                <p className="truncate text-xl font-bold sm:text-2xl">
                  {stockOverview?.low_stock_count != null ? stockOverview.low_stock_count : alerts.length}
                </p>
                <p className="text-xs text-muted-foreground">Low Stock</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 pb-3 px-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-green-50 shrink-0"><Tag className="w-5 h-5 text-green-600" /></div>
              <div className="min-w-0">
                <p className="truncate text-xl font-bold sm:text-2xl">{categories.length}</p>
                <p className="text-xs text-muted-foreground">Categories</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 pb-3 px-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-purple-50 shrink-0"><TrendingUp className="w-5 h-5 text-purple-600" /></div>
              <div className="min-w-0">
                <p
                  className="truncate text-base font-bold sm:text-2xl"
                  title={stockOverview?.total_value != null ? formatCurrency(stockOverview.total_value) : undefined}
                >
                  {stockOverview?.total_value != null ? compactCurrency(stockOverview.total_value) : '—'}
                </p>
                <p className="text-xs text-muted-foreground">Stock Value</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {deductionFailures.length > 0 && (
        <div className="rounded-lg border border-red-300 bg-red-50 p-3 text-sm dark:border-red-900 dark:bg-red-950/30">
          <div className="font-medium text-red-800 dark:text-red-200">
            {deductionFailures.length} stock deduction{deductionFailures.length === 1 ? '' : 's'} failed at sale time — that stock
            wasn&apos;t decremented. Adjust the affected item(s) manually, then dismiss.
          </div>
          <ul className="mt-2 space-y-1">
            {deductionFailures.slice(0, 5).map(f => (
              <li key={f.id} className="flex items-center justify-between gap-3 text-xs text-red-900/80 dark:text-red-100/80">
                <span>
                  {f.product_name ?? 'Item'}{f.order_number ? ` · #${f.order_number}` : ''} — {f.reason ?? 'failed'}
                </span>
                <button
                  type="button"
                  className="shrink-0 rounded border border-red-300 px-2 py-0.5 hover:bg-red-100 disabled:opacity-50 dark:border-red-800 dark:hover:bg-red-900/40"
                  disabled={resolveFailure.isPending}
                  onClick={() => resolveFailure.mutate(f.id)}
                >
                  Dismiss
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="no-scrollbar flex max-w-full gap-1 overflow-x-auto border-b pb-0 [&>*]:shrink-0">
        {tabs.map(t => (
          <button key={t.id} onClick={() => { setTab(t.id); setItemPage(1); setMovPage(1) }}
            className={`flex items-center gap-2 whitespace-nowrap px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${tab === t.id ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground'}`}>
            {t.icon} {t.label}
            {t.badge != null && t.badge > 0 && <Badge variant="destructive" className="ml-1 text-xs px-1.5 py-0">{t.badge}</Badge>}
          </button>
        ))}
      </div>

      {tab === 'items' && <ItemsTab items={items} categories={categories} loading={itemsLoading}
        search={search} setSearch={setSearch} filterCategory={filterCategory} setFilterCategory={setFilterCategory}
        filterStatus={filterStatus} setFilterStatus={setFilterStatus}
        pagination={itemsPagination} total={itemsTotal} setModal={setModal}
        selectedIds={selectedIds} setSelectedIds={setSelectedIds}
        itemSortKey={itemSortKey} itemSortDir={itemSortDir} onItemSort={handleItemSort} />}
      {tab === 'purchasing' && <InventoryPurchasingTab stockItems={catalogPickItems} showToast={showToast} />}
      {tab === 'vendors' && <VendorAPTab showToast={showToast} />}
      {tab === 'alerts' && <AlertsTab alerts={alerts} setModal={setModal} items={lowStockItemsForAlerts} />}
      {tab === 'movements' && (
        <MovementsTab
          movements={movements}
          meta={movMeta}
          page={movPage}
          setPage={setMovPage}
          movType={movType}
          setMovType={setMovType}
          movFrom={movFrom}
          setMovFrom={setMovFrom}
          movTo={movTo}
          setMovTo={setMovTo}
          movCategory={movCategory}
          setMovCategory={setMovCategory}
          categories={categories}
          showToast={showToast}
        />
      )}
      {tab === 'reports' && <ReportsTab report={advancedReport} loading={reportLoading} period={reportPeriod} setPeriod={setReportPeriod} />}
      {tab === 'vendor-reports' && <VendorReportsTab showToast={showToast} />}


      {modal.kind !== 'none' && <ModalOverlay onClose={() => setModal({ kind: 'none' })}>
        {modal.kind === 'editItem' && <EditItemForm item={modal.item} categories={categories} onClose={() => setModal({ kind: 'none' })} qc={qc} showToast={showToast} />}
        {modal.kind === 'purchase' && <PurchaseForm item={modal.item} onClose={() => setModal({ kind: 'none' })} qc={qc} showToast={showToast} />}
        {modal.kind === 'issue' && <IssueForm item={modal.item} users={users} onClose={() => setModal({ kind: 'none' })} qc={qc} showToast={showToast} />}
        {modal.kind === 'supplierReturn' && <ReturnToSupplierForm item={modal.item} onClose={() => setModal({ kind: 'none' })} qc={qc} showToast={showToast} />}
        {modal.kind === 'adjust' && <AdjustForm item={modal.item} onClose={() => setModal({ kind: 'none' })} qc={qc} showToast={showToast} />}
        {modal.kind === 'count' && <RecordCountForm item={modal.item} onClose={() => setModal({ kind: 'none' })} qc={qc} showToast={showToast} />}
      </ModalOverlay>}

      <div className="fixed bottom-4 right-4 z-[60] space-y-2">
        {toasts.map(t => (
          <div key={t.id} className={`flex items-center gap-2 px-4 py-3 rounded-lg shadow-lg text-sm font-medium animate-in slide-in-from-right ${t.type === 'success' ? 'bg-green-600 text-white' : 'bg-red-600 text-white'}`}>
            {t.type === 'success' ? <CheckCircle2 className="w-4 h-4 shrink-0" /> : <XCircle className="w-4 h-4 shrink-0" />}
            {t.message}
          </div>
        ))}
      </div>
    </div>
  )
}

// ─── Meatball / 3-dot menu ───────────────────────────────────────

function MeatballMenu({ children }: { children: React.ReactNode }) {
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) return
    function handleClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', handleClick)
    return () => document.removeEventListener('mousedown', handleClick)
  }, [open])

  return (
    <div className="relative shrink-0" ref={ref}>
      <button onClick={() => setOpen(o => !o)} className="p-1 rounded hover:bg-muted transition-colors">
        <MoreVertical className="w-4 h-4 text-muted-foreground" />
      </button>
      {open && (
        <div className="absolute right-0 top-full mt-1 z-30 bg-popover border rounded-lg shadow-lg py-1 min-w-[140px]"
          onClick={() => setOpen(false)}>
          {children}
        </div>
      )}
    </div>
  )
}

function MenuItem({ icon, label, destructive, onClick }: { icon: React.ReactNode; label: string; destructive?: boolean; onClick: () => void }) {
  return (
    <button onClick={onClick}
      className={`flex items-center gap-2 w-full px-3 py-2 text-sm hover:bg-muted transition-colors ${destructive ? 'text-red-600 hover:bg-red-50' : ''}`}>
      {icon}
      {label}
    </button>
  )
}

// ─── Items Tab (redesigned) ──────────────────────────────────────

type ItemsTabProps = {
  items: StockItem[]
  categories: StockCategory[]
  loading: boolean
  search: string
  setSearch: (s: string) => void
  filterCategory: string
  setFilterCategory: (s: string) => void
  filterStatus: StockStatusFilter
  setFilterStatus: (s: StockStatusFilter) => void
  pagination: PaginationControls
  total: number
  setModal: (m: ModalState) => void
  selectedIds: Set<string>
  setSelectedIds: Dispatch<SetStateAction<Set<string>>>
  itemSortKey: ItemSortKey
  itemSortDir: 'asc' | 'desc'
  onItemSort: (key: ItemSortKey) => void
}

function ItemsTab({
  items,
  categories,
  loading,
  search,
  setSearch,
  filterCategory,
  setFilterCategory,
  filterStatus,
  setFilterStatus,
  pagination,
  total,
  setModal,
  selectedIds,
  setSelectedIds,
  itemSortKey,
  itemSortDir,
  onItemSort,
}: ItemsTabProps) {
  const { formatCurrency } = useCurrency()
  const [colOrder, setColOrder] = useState<ItemsDataColumnId[]>(() => {
    if (typeof window === 'undefined') return DEFAULT_ITEM_COL_ORDER
    return parseStoredItemColOrder(localStorage.getItem(ITEM_COL_ORDER_STORAGE_KEY)) ?? DEFAULT_ITEM_COL_ORDER
  })

  function persistColOrder(next: ItemsDataColumnId[]) {
    try {
      localStorage.setItem(ITEM_COL_ORDER_STORAGE_KEY, JSON.stringify(next))
    } catch {
      /* ignore quota / private mode */
    }
  }

  function moveDataColumn(dragId: ItemsDataColumnId, dropId: ItemsDataColumnId) {
    if (dragId === dropId) return
    setColOrder((prev) => {
      const i = prev.indexOf(dragId)
      const j = prev.indexOf(dropId)
      if (i < 0 || j < 0) return prev
      const next = [...prev]
      next.splice(i, 1)
      next.splice(j, 0, dragId)
      persistColOrder(next)
      return next
    })
  }

  function sortIcon(col: ItemSortKey) {
    if (itemSortKey !== col) return <ArrowUpDown className="w-4 h-4 shrink-0 opacity-40" aria-hidden />
    return itemSortDir === 'asc' ? (
      <ArrowUp className="w-4 h-4 shrink-0" aria-hidden />
    ) : (
      <ArrowDown className="w-4 h-4 shrink-0" aria-hidden />
    )
  }

  const allChecked = items.length > 0 && items.every((i: StockItem) => selectedIds.has(i.id))
  const someChecked = selectedIds.size > 0

  function toggleAll() {
    if (allChecked) {
      setSelectedIds(new Set())
    } else {
      setSelectedIds(new Set(items.map((i: StockItem) => i.id)))
    }
  }

  function toggleOne(id: string) {
    setSelectedIds((prev: Set<string>) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id); else next.add(id)
      return next
    })
  }



  return (
    <div className="space-y-4">
      {/* Search + Filter bar */}
      <div className="flex flex-wrap gap-3 items-center">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="w-5 h-5 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <input value={search} onChange={e => { setSearch(e.target.value); pagination.goToFirstPage() }} placeholder="Search items..."
            className="w-full h-11 pl-10 pr-3 border rounded-md text-base bg-background" />
        </div>
        <select value={filterCategory} onChange={e => { setFilterCategory(e.target.value); pagination.goToFirstPage() }}
          className="border rounded-md px-3 h-11 text-base bg-background min-w-[160px]">
          <option value="">All Categories</option>
          {categories.map((c: StockCategory) => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
        <select value={filterStatus} onChange={e => { setFilterStatus(e.target.value as StockStatusFilter); pagination.goToFirstPage() }}
          className="border rounded-md px-3 h-11 text-base bg-background min-w-[140px]">
          <option value="">All Status</option>
          <option value="low">Low Stock</option>
          <option value="ok">OK</option>
        </select>
      </div>

      {/* Bulk actions bar */}
      {someChecked && (
        <div className="flex flex-wrap items-center gap-3 gap-y-2 bg-primary/5 border border-primary/20 rounded-lg px-4 py-2.5">
          <span className="text-base font-medium">{selectedIds.size} item{selectedIds.size > 1 ? 's' : ''} selected</span>
          <div className="flex-1" />
          <Button
            size="sm"
            variant="outline"
            className="h-9 text-sm"
            disabled={selectedIds.size !== 1}
            title={
              selectedIds.size !== 1
                ? 'Select exactly one item to record a purchase for that line'
                : 'Record purchase for the selected item'
            }
            onClick={() => {
              const first = items.find((i: StockItem) => selectedIds.has(i.id))
              if (first) setModal({ kind: 'purchase', item: first })
            }}
          >
            <ArrowDownCircle className="w-4 h-4 mr-1" /> Record purchase
          </Button>
          <Button size="sm" variant="ghost" className="h-9 text-sm" onClick={() => setSelectedIds(new Set())}>
            Clear
          </Button>
        </div>
      )}

      {/* Table — sortable headers + draggable column order (saved locally) */}
      <div className="border rounded-lg overflow-x-auto bg-card">
        <table className="w-full text-base">
          <thead className="bg-muted/50 border-b">
            <tr>
              <th className="w-12 px-3 py-3.5 align-middle">
                <input type="checkbox" checked={allChecked} onChange={toggleAll}
                  className="h-4 w-4 rounded border-input cursor-pointer" aria-label="Select all rows" />
              </th>
              {colOrder.map((col) => {
                const align = ITEM_COL_ALIGN[col]
                return (
                  <th
                    key={col}
                    className={`px-4 py-3.5 align-middle ${align === 'right' ? 'text-right' : 'text-left'}`}
                    onDragOver={(e) => {
                      e.preventDefault()
                      e.dataTransfer.dropEffect = 'move'
                    }}
                    onDrop={(e) => {
                      e.preventDefault()
                      const from = e.dataTransfer.getData(INV_COL_DND) as ItemsDataColumnId
                      if (from && DEFAULT_ITEM_COL_ORDER.includes(from)) moveDataColumn(from, col)
                    }}
                  >
                    <div className={`flex items-center gap-1.5 min-w-0 ${align === 'right' ? 'justify-end' : ''}`}>
                      <button
                        type="button"
                        className="touch-none shrink-0 rounded-md p-1 text-muted-foreground hover:bg-muted hover:text-foreground cursor-grab active:cursor-grabbing"
                        aria-label={`Reorder ${ITEM_COL_LABELS[col]} column`}
                        title="Drag to reorder columns"
                        draggable
                        onDragStart={(ev) => {
                          ev.dataTransfer.setData(INV_COL_DND, col)
                          ev.dataTransfer.effectAllowed = 'move'
                        }}
                      >
                        <GripVertical className="w-4 h-4" />
                      </button>
                      <button
                        type="button"
                        className="flex min-w-0 items-center gap-1.5 font-semibold text-sm tracking-tight text-foreground hover:text-primary"
                        onClick={() => onItemSort(col)}
                      >
                        <span className="truncate">{ITEM_COL_LABELS[col]}</span>
                        {sortIcon(col)}
                      </button>
                    </div>
                  </th>
                )
              })}
              <th className="text-center px-4 py-3.5 text-sm font-semibold tracking-tight whitespace-nowrap min-w-[280px]">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {loading && <tr><td colSpan={8} className="px-4 py-10 text-center text-muted-foreground text-base">Loading…</td></tr>}
            {!loading && items.length === 0 && <tr><td colSpan={8} className="px-4 py-10 text-center text-muted-foreground text-base">No items found</td></tr>}
            {items.map((item: StockItem) => {
              const low = item.quantity_on_hand <= item.reorder_level
              return (
                <tr key={item.id} className={`hover:bg-muted/30 ${selectedIds.has(item.id) ? 'bg-primary/5' : ''}`}>
                  <td className="w-12 px-3 py-3.5 align-middle">
                    <input type="checkbox" checked={selectedIds.has(item.id)} onChange={() => toggleOne(item.id)}
                      className="h-4 w-4 rounded border-input cursor-pointer" aria-label={`Select ${item.name}`} />
                  </td>
                  {colOrder.map((col) => {
                    const align = ITEM_COL_ALIGN[col]
                    const cellClass = `px-4 py-3.5 align-middle ${align === 'right' ? 'text-right' : 'text-left'}`
                    if (col === 'name') {
                      return (
                        <td key={col} className={cellClass}>
                          <div className="font-semibold">{item.name}</div>
                          <div className="text-sm text-muted-foreground">{item.unit}</div>
                        </td>
                      )
                    }
                    if (col === 'category') {
                      return (
                        <td key={col} className={`${cellClass} text-muted-foreground`}>
                          {item.category?.name ?? '—'}
                        </td>
                      )
                    }
                    if (col === 'on_hand') {
                      return (
                        <td key={col} className={cellClass}>
                          <span className={`inline-flex items-center justify-end gap-1.5 font-semibold tabular-nums ${low ? 'text-red-600' : 'text-green-700'}`}>
                            {!low && <span className="w-2 h-2 rounded-full bg-green-500 inline-block" aria-hidden />}
                            {item.quantity_on_hand} {item.unit}
                            {low && <Badge variant="destructive" className="text-xs px-1.5 py-0 ml-1">LOW</Badge>}
                          </span>
                        </td>
                      )
                    }
                    if (col === 'reorder') {
                      return (
                        <td key={col} className={`${cellClass} text-muted-foreground tabular-nums`}>
                          {item.reorder_level} {item.unit}
                        </td>
                      )
                    }
                    if (col === 'unit_cost') {
                      return (
                        <td key={col} className={`${cellClass} tabular-nums`}>
                          {item.default_unit_cost != null ? formatCurrency(item.default_unit_cost) : '—'}
                        </td>
                      )
                    }
                    return (
                      <td key={col} className={`${cellClass} text-muted-foreground whitespace-nowrap tabular-nums`}>
                        {item.earliest_expiry ?? '—'}
                      </td>
                    )
                  })}
                  <td className="px-4 py-3.5 align-middle whitespace-nowrap">
                    <div className="flex flex-nowrap items-center justify-center gap-1.5">
                      <Button size="sm" variant="outline" className="h-9 shrink-0 text-sm" onClick={() => setModal({ kind: 'purchase', item })}>
                        <ArrowDownCircle className="w-4 h-4 mr-1" /> Purchase
                      </Button>
                      <Button size="sm" variant="outline" className="h-9 shrink-0 text-sm" onClick={() => setModal({ kind: 'issue', item })}>
                        <ArrowUpCircle className="w-4 h-4 mr-1" /> Issue
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-9 shrink-0 text-sm"
                        disabled={item.quantity_on_hand <= 0}
                        title={item.quantity_on_hand <= 0 ? 'No stock on hand to return' : undefined}
                        onClick={() => setModal({ kind: 'supplierReturn', item })}
                      >
                        <RotateCcw className="w-4 h-4 mr-1" /> Return
                      </Button>
                      <Button size="sm" variant="outline" className="h-9 shrink-0 text-sm" onClick={() => setModal({ kind: 'adjust', item })}>
                        <SlidersHorizontal className="w-4 h-4 mr-1" /> Adjust
                      </Button>
                      <MeatballMenu>
                        <MenuItem icon={<ClipboardCheck className="w-3.5 h-3.5" />} label="Record count" onClick={() => setModal({ kind: 'count', item })} />
                        <MenuItem icon={<Pencil className="w-3.5 h-3.5" />} label="Edit" onClick={() => setModal({ kind: 'editItem', item })} />
                      </MeatballMenu>
                    </div>
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
      <PaginationControlsComponent pagination={pagination} total={total} className="pt-4 flex-wrap gap-y-2" />

    </div>
  )
}

// ─── Other tab components (unchanged) ────────────────────────────

function AlertsTab({ alerts, setModal, items }: { alerts: StockAlert[]; setModal: (m: ModalState) => void; items: StockItem[] }) {
  if (alerts.length === 0) return (
    <div className="text-center py-12 text-muted-foreground">
      <AlertTriangle className="w-10 h-10 mx-auto mb-3 text-green-500" />
      <p className="font-medium">All stock levels are healthy</p>
    </div>
  )
  return (
    <div className="space-y-3">
      {alerts.map(a => {
        const matchedItem = items.find((i: StockItem) => i.id === a.id)
        return (
          <Card key={a.id} className="border-red-200 dark:border-red-900/40 bg-red-50/30 dark:bg-red-950/20">
            <CardContent className="py-3 px-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <AlertTriangle className="w-5 h-5 text-red-500 shrink-0" />
                <div>
                  <p className="font-medium">{a.name}</p>
                  <p className="text-xs text-muted-foreground">{a.category_name} &middot; {a.quantity_on_hand} {a.unit} remaining (reorder at {a.reorder_level})</p>
                </div>
              </div>
              {matchedItem && (
                <Button size="sm" variant="outline" className="text-xs h-7 shrink-0" onClick={() => setModal({ kind: 'purchase', item: matchedItem })}>
                  <ShoppingCart className="w-3 h-3 mr-1" /> Restock
                </Button>
              )}
            </CardContent>
          </Card>
        )
      })}
    </div>
  )
}

function MovementsTab({
  movements,
  meta,
  page,
  setPage,
  movType,
  setMovType,
  movFrom,
  setMovFrom,
  movTo,
  setMovTo,
  movCategory,
  setMovCategory,
  categories,
  showToast,
}: {
  movements: StockMovement[]
  meta?: { total_pages: number }
  page: number
  setPage: (p: number) => void
  movType: string
  setMovType: (v: string) => void
  movFrom: string
  setMovFrom: (v: string) => void
  movTo: string
  setMovTo: (v: string) => void
  movCategory: string
  setMovCategory: (v: string) => void
  categories: StockCategory[]
  showToast: (type: 'success' | 'error', message: string) => void
}) {
  const qc = useQueryClient()
  const { formatCurrency, currencyCode } = useCurrency()
  const [voidTarget, setVoidTarget] = useState<StockMovement | null>(null)
  const [voidReason, setVoidReason] = useState('')
  const [costTarget, setCostTarget] = useState<StockMovement | null>(null)
  const [costValue, setCostValue] = useState('')
  const [costReason, setCostReason] = useState('')

  const voidMut = useMutation({
    mutationFn: () => apiClient.voidPurchaseMovement(voidTarget!.id, { reason: voidReason.trim() }),
    onSuccess: res => {
      if (res.success) {
        showToast('success', res.message || 'Purchase voided')
        setVoidTarget(null)
        setVoidReason('')
        qc.invalidateQueries({ queryKey: ['stockMovements'] })
        qc.invalidateQueries({ queryKey: ['stockItems'] })
          qc.invalidateQueries({ queryKey: ['stockSummary'] })
        qc.invalidateQueries({ queryKey: ['advancedStockReport'] })
        qc.invalidateQueries({ queryKey: ['stockAlerts'] })
      } else {
        showToast('error', res.message || 'Could not void purchase')
      }
    },
    onError: (err: Error) => showToast('error', err.message || 'Void failed'),
  })

  const correctMut = useMutation({
    mutationFn: () =>
      apiClient.correctPurchaseMovementCost(costTarget!.id, {
        unit_cost: Number(costValue),
        reason: costReason.trim(),
      }),
    onSuccess: res => {
      if (res.success) {
        showToast('success', res.message || 'Cost updated')
        setCostTarget(null)
        setCostValue('')
        setCostReason('')
        qc.invalidateQueries({ queryKey: ['stockMovements'] })
        qc.invalidateQueries({ queryKey: ['stockItems'] })
          qc.invalidateQueries({ queryKey: ['stockSummary'] })
        qc.invalidateQueries({ queryKey: ['advancedStockReport'] })
      } else {
        showToast('error', res.message || 'Could not update cost')
      }
    },
    onError: (err: Error) => showToast('error', err.message || 'Correction failed'),
  })

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-3 items-center">
        <select
          value={movCategory}
          onChange={e => {
            setMovCategory(e.target.value)
            setPage(1)
          }}
          className="border rounded-md px-3 py-2 text-sm bg-background min-w-[140px]"
        >
          <option value="">All Categories</option>
          {categories.map((c: StockCategory) => (
            <option key={c.id} value={c.id}>
              {c.name}
            </option>
          ))}
        </select>
        <select value={movType} onChange={e => { setMovType(e.target.value); setPage(1) }} className="border rounded-md px-3 py-2 text-sm bg-background">
          <option value="">All Types</option>
          <option value="purchase">Purchase</option>
          <option value="issue">Issue</option>
          <option value="adjustment">Adjustment</option>
          <option value="supplier_return">Supplier return</option>
        </select>
        <DatePicker
          className="w-[180px]"
          value={movFrom}
          onChange={v => { setMovFrom(v); setPage(1) }}
          max={movTo || undefined}
        />
        <span className="text-sm text-muted-foreground">to</span>
        <DatePicker
          className="w-[180px]"
          value={movTo}
          onChange={v => { setMovTo(v); setPage(1) }}
          min={movFrom || undefined}
        />
      </div>

      <div className="border rounded-lg overflow-hidden overflow-x-auto">
        <table className="w-full text-sm min-w-[920px]">
          <thead className="bg-muted/50">
            <tr>
              <th className="text-left px-4 py-3 font-medium">When</th>
              <th className="text-left px-4 py-3 font-medium">Item</th>
              <th className="text-left px-4 py-3 font-medium">Type</th>
              <th className="text-right px-4 py-3 font-medium">Qty</th>
              <th className="text-right px-4 py-3 font-medium">Cost</th>
              <th className="text-left px-4 py-3 font-medium">Issued To</th>
              <th className="text-left px-4 py-3 font-medium">By</th>
              <th className="text-left px-4 py-3 font-medium">Note</th>
              <th className="text-right px-4 py-3 font-medium w-[120px]">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {movements.length === 0 && (
              <tr>
                <td colSpan={9} className="px-4 py-8 text-center text-muted-foreground">No movements found</td>
              </tr>
            )}
            {movements.map((m: StockMovement) => {
              const voided = Boolean(m.voided_at)
              const canFix = m.movement_type === 'purchase' && m.purchase_can_void && !voided
              return (
                <tr key={m.id} className={`hover:bg-muted/30 ${voided ? 'opacity-60' : ''}`}>
                  <td className="px-4 py-2 text-muted-foreground whitespace-nowrap">
                    {formatDateTimeHuman(m.created_at)}
                    {voided && (
                      <Badge variant="outline" className="ml-2 text-[10px] px-1 py-0">Voided</Badge>
                    )}
                  </td>
                  <td className={`px-4 py-2 font-medium ${voided ? 'line-through' : ''}`}>{m.item_name}</td>
                  <td className="px-4 py-2">
                    <Badge
                      variant={m.movement_type === 'purchase' ? 'default' : m.movement_type === 'issue' ? 'secondary' : m.movement_type === 'supplier_return' ? 'warning' : 'outline'}
                      className="text-xs capitalize"
                    >
                      {m.movement_type.replace(/_/g, ' ')}
                    </Badge>
                  </td>
                  <td className={`px-4 py-2 text-right font-medium ${m.quantity < 0 ? 'text-red-600' : 'text-green-600'} ${voided ? 'line-through' : ''}`}>
                    {m.quantity > 0 ? '+' : ''}{m.quantity} {m.item_unit}
                  </td>
                  <td className={`px-4 py-2 text-right ${voided ? 'line-through' : ''}`}>{m.total_cost != null ? formatCurrency(m.total_cost) : '—'}</td>
                  <td className="px-4 py-2 text-muted-foreground">{m.issued_to_name ?? '—'}</td>
                  <td className="px-4 py-2 text-muted-foreground">{m.created_by_name ?? '—'}</td>
                  <td className="px-4 py-2 text-muted-foreground max-w-[160px] truncate" title={m.note ?? ''}>
                    {voided && m.void_reason ? `${m.void_reason} · ` : ''}{m.note ?? ''}
                  </td>
                  <td className="px-4 py-2 text-right whitespace-nowrap">
                    {canFix && (
                      <span className="inline-flex gap-1 justify-end">
                        <Button type="button" size="sm" variant="outline" className="h-7 px-2 text-xs" onClick={() => {
                          setCostTarget(m)
                          setCostValue(m.unit_cost != null ? String(m.unit_cost) : '')
                          setCostReason('')
                        }}>
                          <Pencil className="w-3 h-3 mr-0.5" /> Cost
                        </Button>
                        <Button type="button" size="sm" variant="outline" className="h-7 px-2 text-xs text-destructive border-destructive/40" onClick={() => {
                          setVoidTarget(m)
                          setVoidReason('')
                        }}>
                          <Undo2 className="w-3 h-3 mr-0.5" /> Void
                        </Button>
                      </span>
                    )}
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
      {meta && meta.total_pages > 1 && <Pagination page={page} totalPages={meta.total_pages} setPage={setPage} />}

      <Dialog open={voidTarget != null} onOpenChange={open => { if (!open) { setVoidTarget(null); setVoidReason('') } }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Void purchase</DialogTitle>
            <DialogDescription>
              Reverses on-hand quantity, removes the FIFO lot, and deletes the linked expense. Only allowed when nothing has been issued from this receipt.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <p className="text-sm font-medium">{voidTarget?.item_name}</p>
            <label className="text-sm font-medium">Reason (required)</label>
            <Input value={voidReason} onChange={e => setVoidReason(e.target.value)} placeholder="e.g. entered wrong item" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setVoidTarget(null); setVoidReason('') }}>Cancel</Button>
            <Button
              variant="destructive"
              disabled={voidReason.trim().length === 0 || voidMut.isPending}
              onClick={() => voidMut.mutate()}
            >
              {voidMut.isPending ? 'Voiding…' : 'Void purchase'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={costTarget != null} onOpenChange={open => { if (!open) { setCostTarget(null); setCostValue(''); setCostReason('') } }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Correct unit cost</DialogTitle>
            <DialogDescription>
              Updates this movement, its stock lot, and the linked expense. Use when the quantity was right but the price was wrong.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <p className="text-sm font-medium">{costTarget?.item_name}</p>
            <div>
              <label className="text-sm font-medium">New unit cost ({currencyCode})</label>
              <Input type="number" step="0.01" min={0} value={costValue} onChange={e => setCostValue(e.target.value)} className="mt-1" />
            </div>
            <div>
              <label className="text-sm font-medium">Reason (required)</label>
              <Input value={costReason} onChange={e => setCostReason(e.target.value)} placeholder="e.g. invoice showed different price" className="mt-1" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setCostTarget(null); setCostValue(''); setCostReason('') }}>Cancel</Button>
            <Button
              disabled={
                costReason.trim().length === 0
                || costValue === ''
                || Number.isNaN(Number(costValue))
                || Number(costValue) < 0
                || correctMut.isPending
              }
              onClick={() => correctMut.mutate()}
            >
              {correctMut.isPending ? 'Saving…' : 'Save correction'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}

const DONUT_COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899', '#06b6d4', '#84cc16']

/**
 * Tiny contextual-help affordance used throughout the Reports tab. Shows an
 * accessible Eye icon trigger with a short tooltip explanation — consistent
 * with how enterprise BI tools (Looker, Metabase, Tableau) surface metric
 * definitions.
 */
function MetricHint({ text, label }: { text: string; label?: string }) {
  return (
    <UITooltip>
      <TooltipTrigger asChild>
        <button
          type="button"
          aria-label={label ? `About ${label}` : 'More info'}
          className="inline-flex items-center justify-center rounded-full p-0.5 text-muted-foreground/60 hover:text-foreground hover:bg-muted/50 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          <Eye className="w-3.5 h-3.5" />
        </button>
      </TooltipTrigger>
      <TooltipContent>{text}</TooltipContent>
    </UITooltip>
  )
}

function ReportsTab({ report, loading, period, setPeriod }: {
  report: AdvancedStockReport | undefined; loading: boolean;
  period: string; setPeriod: (p: string) => void;
}) {
  const { formatCurrency, currencyCode } = useCurrency()
  const [exporting, setExporting] = useState(false)
  const [exportError, setExportError] = useState<string | null>(null)
  // Export date range — the same combined presets+calendar control used on the
  // Reports & Analytics page, independent of the on-screen KPI period.
  const exportRange = useReportRange()

  // sheet omitted / 'all' → full multi-sheet workbook; a slug → that one report.
  const handleExport = useCallback(async (sheet?: string) => {
    setExportError(null)
    setExporting(true)
    try {
      await apiClient.exportInventoryReportsXlsx(exportRange.fromISO, exportRange.toISO, sheet)
    } catch {
      setExportError('Export failed. Please try again.')
    } finally {
      setExporting(false)
    }
  }, [exportRange.fromISO, exportRange.toISO])

  // The exportable inventory reports, each downloadable on its own.
  const INVENTORY_REPORTS: { slug: string; label: string }[] = [
    { slug: 'stock', label: 'Stock report' },
    { slug: 'purchase_orders', label: 'Purchase orders' },
    { slug: 'sales', label: 'Sales (by item / channel)' },
    { slug: 'consumption_variance', label: 'Consumption variance' },
    { slug: 'vendor_aging', label: 'Vendor aging (AP)' },
    { slug: 'vendor_ledger', label: 'Vendor ledger' },
  ]

  if (loading || !report) return (
    <div className="text-center py-16 text-muted-foreground">
      <RefreshCw className="w-8 h-8 mx-auto mb-3 animate-spin opacity-50" />
      <p className="font-medium">Loading report data...</p>
    </div>
  )

  const { kpis, category_values, trends, waste, variance } = report

  const catData = category_values ?? []
  const totalCatValue = catData.reduce((s, c) => s + c.value, 0)
  const catDataWithPct = catData.map(c => ({ ...c, pct: totalCatValue > 0 ? (c.value / totalCatValue * 100) : 0 }))

  const trendData = trends ?? []
  const wasteData = waste ?? []

  const periodLabel = period === '7' ? 'Last 7 days' :
    period === '14' ? 'Last 14 days' :
    period === '30' ? 'Last 30 days' :
    period === '60' ? 'Last 60 days' :
    period === '90' ? 'Last 90 days' : `Last ${period} days`

  const wasteTotal = wasteData.reduce((s, w) => s + w.lost_value, 0)

  // Consumption variance from real physical counts only. Rows with a recorded
  // count surface counted vs book + the variance; rows without are summarised as
  // "not counted" so the table never implies a verified-balanced item.
  const varianceData = variance ?? []
  const countedRows = varianceData.filter(v => v.has_count)
  const uncountedCount = varianceData.length - countedRows.length

  return (
    <div className="space-y-6 pb-12">
      {/* ── Header & period picker ──────────────────────────── */}
      <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-3">
        <div className="space-y-1.5 max-w-3xl">
          <div className="flex items-center gap-2">
            <h3 className="text-lg font-semibold">Inventory intelligence</h3>
            <MetricHint text={ReportHelp.REPORTS_TAB_INTRO} label="inventory intelligence" />
          </div>
          <p className="text-sm text-muted-foreground leading-relaxed">
            {ReportHelp.REPORTS_TAB_INTRO}
          </p>
        </div>
        <div className="flex flex-col items-stretch sm:items-end gap-2">
          <div className="flex items-center gap-2">
            <span className="text-xs text-muted-foreground hidden sm:inline">Period</span>
            <Select value={period} onValueChange={setPeriod}>
              <SelectTrigger className="w-[170px] h-9">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="7">Last 7 days</SelectItem>
                <SelectItem value="14">Last 14 days</SelectItem>
                <SelectItem value="30">Last 30 days</SelectItem>
                <SelectItem value="60">Last 60 days</SelectItem>
                <SelectItem value="90">Last 90 days</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {/* Export range — combined presets + calendar (same as Reports & Analytics). */}
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-xs text-muted-foreground">Export range</span>
            <DateRangeFilter range={exportRange} />
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm" className="h-9" disabled={exporting}>
                  <Download className="w-4 h-4 mr-1.5" />
                  {exporting ? 'Exporting…' : 'Export to Excel'}
                  <ChevronDown className="w-3.5 h-3.5 ml-1.5 opacity-70" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuItem onClick={() => handleExport('all')}>
                  <FileSpreadsheet className="w-4 h-4 mr-2" /> All reports (one workbook)
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuLabel className="text-xs">Export a single report</DropdownMenuLabel>
                {INVENTORY_REPORTS.map(r => (
                  <DropdownMenuItem key={r.slug} onClick={() => handleExport(r.slug)}>
                    {r.label}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
          {exportError && <span className="text-xs text-red-600">{exportError}</span>}
        </div>
      </div>

      {/* ── KPI Cards (8-up, responsive) ─────────────────────── */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiCard
          icon={<DollarSign className="w-5 h-5 text-blue-600" />}
          tint="bg-blue-50"
          label="Total stock value"
          value={formatCurrency(kpis.total_stock_value)}
          hint={ReportHelp.KPI_TOTAL_STOCK_VALUE}
        />
        <KpiCard
          icon={<Recycle className={`w-5 h-5 ${kpis.total_waste_value > 0 ? 'text-red-600' : 'text-gray-400'}`} />}
          tint={kpis.total_waste_value > 0 ? 'bg-red-50' : 'bg-gray-50'}
          label="Waste & spoilage loss"
          value={formatCurrency(kpis.total_waste_value)}
          valueTone={kpis.total_waste_value > 0 ? 'text-red-600' : ''}
          cardClass={kpis.total_waste_value > 0 ? 'border-red-200' : ''}
          hint={ReportHelp.KPI_WASTE_VALUE}
          footer={periodLabel}
        />
        <KpiCard
          icon={<RefreshCw className="w-5 h-5 text-purple-600" />}
          tint="bg-purple-50"
          label="Inventory turnover"
          value={`${kpis.turnover_rate.toFixed(2)}x`}
          hint={ReportHelp.KPI_TURNOVER}
          footer={periodLabel}
        />
        <KpiCard
          icon={<ArrowDownCircle className="w-5 h-5 text-emerald-600" />}
          tint="bg-emerald-50"
          label="Issued value (period)"
          value={formatCurrency(kpis.issued_value_period ?? 0)}
          hint={ReportHelp.KPI_ISSUED_VALUE}
          footer={periodLabel}
        />
        <KpiCard
          icon={<CalendarClock className="w-5 h-5 text-sky-600" />}
          tint="bg-sky-50"
          label="Est. days of cover"
          value={
            kpis.days_cover_estimate == null
              ? '—'
              : kpis.days_cover_estimate > 9999
                ? '9,999+'
                : `${Math.round(kpis.days_cover_estimate)} days`
          }
          hint={ReportHelp.KPI_DAYS_COVER}
          footer={kpis.days_cover_estimate == null ? 'No issues in period' : undefined}
        />
        <KpiCard
          icon={<Percent className={`w-5 h-5 ${(kpis.waste_pct_of_issued ?? 0) > 4 ? 'text-red-600' : 'text-amber-600'}`} />}
          tint={(kpis.waste_pct_of_issued ?? 0) > 4 ? 'bg-red-50' : 'bg-amber-50'}
          label="Waste vs issued value"
          value={kpis.waste_pct_of_issued == null ? '—' : `${(kpis.waste_pct_of_issued).toFixed(2)}%`}
          valueTone={(kpis.waste_pct_of_issued ?? 0) > 4 ? 'text-red-600' : ''}
          hint={ReportHelp.KPI_WASTE_PCT_ISSUED}
          footer={periodLabel}
        />
        <KpiCard
          icon={<AlertCircle className={`w-5 h-5 ${(kpis.low_stock_count ?? 0) > 0 ? 'text-amber-600' : 'text-gray-400'}`} />}
          tint={(kpis.low_stock_count ?? 0) > 0 ? 'bg-amber-50' : 'bg-gray-50'}
          label="Low stock items"
          value={String(kpis.low_stock_count ?? 0)}
          valueTone={(kpis.low_stock_count ?? 0) > 0 ? 'text-amber-700' : ''}
          cardClass={(kpis.low_stock_count ?? 0) > 0 ? 'border-amber-200' : ''}
          hint={ReportHelp.KPI_LOW_STOCK}
          footer="Right now"
        />
      </div>

      {/* ── Charts Row ────────────────────────────────────────── */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Donut: Category Value Distribution */}
        <Card>
          <CardHeader className="pb-2">
            <div className="flex items-center gap-2">
              <CardTitle className="text-base font-semibold">Value distribution by category</CardTitle>
              <MetricHint text={ReportHelp.CHART_CATEGORY_DONUT} label="category distribution" />
            </div>
            <CardDescription className="text-xs">
              Share of current stock value across each category.
            </CardDescription>
          </CardHeader>
          <CardContent>
            {catData.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-10">No categorised stock data.</p>
            ) : (
              <div className="flex items-center gap-4">
                <ResponsiveContainer width="50%" height={240}>
                  <PieChart>
                    <Pie data={catDataWithPct} dataKey="value" nameKey="name" cx="50%" cy="50%"
                      innerRadius={60} outerRadius={95} paddingAngle={2} strokeWidth={0}>
                      {catDataWithPct.map((_, i) => (
                        <Cell key={i} fill={DONUT_COLORS[i % DONUT_COLORS.length]} />
                      ))}
                    </Pie>
                    <ReTooltip formatter={(val: number) => formatCurrency(val)} />
                  </PieChart>
                </ResponsiveContainer>
                <div className="flex-1 space-y-2 text-sm min-w-0">
                  {catDataWithPct.map((c, i) => (
                    <div key={i} className="flex items-center gap-2">
                      <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ background: DONUT_COLORS[i % DONUT_COLORS.length] }} />
                      <span className="truncate flex-1">{c.name}</span>
                      <span className="text-muted-foreground font-medium tabular-nums">{c.pct.toFixed(0)}%</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Dual-Line: Purchase Cost vs Issued Qty */}
        <Card>
          <CardHeader className="pb-2">
            <div className="flex items-center gap-2">
              <CardTitle className="text-base font-semibold">Purchase cost vs issued quantity</CardTitle>
              <MetricHint text={ReportHelp.CHART_PURCHASE_VS_ISSUED} label="purchase vs issued" />
            </div>
            <CardDescription className="text-xs">
              Weekly totals across {periodLabel.toLowerCase()}.
            </CardDescription>
          </CardHeader>
          <CardContent>
            {trendData.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-10">No purchases or issues recorded in this period.</p>
            ) : (
              <ResponsiveContainer width="100%" height={240}>
                <LineChart data={trendData} margin={{ top: 16, right: 10, left: 0, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" className="opacity-30" />
                  <XAxis dataKey="week" tick={{ fontSize: 11 }} />
                  <YAxis yAxisId="cost" tick={{ fontSize: 11 }} tickFormatter={(v) => formatCurrency(Number(v))} />
                  <YAxis yAxisId="qty" orientation="right" tick={{ fontSize: 11 }} />
                  <ReTooltip formatter={(val: number, name: string) => name.startsWith('Purchase') ? formatCurrency(val) : val} />
                  <Legend iconSize={10} wrapperStyle={{ fontSize: 12 }} />
                  <Line yAxisId="cost" type="monotone" dataKey="purchase_cost" name={`Purchase cost (${currencyCode})`} stroke="#3b82f6" strokeWidth={2} dot={{ r: 3 }} />
                  <Line yAxisId="qty" type="monotone" dataKey="issued_qty" name="Issued qty" stroke="#ef4444" strokeWidth={2} dot={{ r: 3 }} />
                </LineChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>
      </div>

      {/* ── Waste & Spoilage Table ────────────────────────────── */}
      <Card className={wasteData.length > 0 ? 'border-red-100' : ''}>
        <CardHeader className="pb-3">
          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
            <div className="space-y-1">
              <div className="flex items-center gap-2">
                <CardTitle className="text-base font-semibold">Waste & spoilage summary</CardTitle>
                <MetricHint text={ReportHelp.WASTE_TABLE_INTRO} label="waste summary" />
              </div>
              <CardDescription className="text-xs max-w-2xl">
                {ReportHelp.WASTE_TABLE_SHORT}
              </CardDescription>
            </div>
            {wasteData.length > 0 && (
              <Badge variant="destructive" className="text-xs whitespace-nowrap self-start">
                {wasteData.length} incident{wasteData.length !== 1 ? 's' : ''} &middot; {formatCurrency(wasteTotal)} lost
              </Badge>
            )}
          </div>
        </CardHeader>
        <CardContent className="pt-0">
          {wasteData.length === 0 ? (
            <div className="text-center py-10 text-muted-foreground">
              <CheckCircle2 className="w-8 h-8 mx-auto mb-2 text-green-500" />
              <p className="text-sm font-medium">No waste or spoilage recorded in this period.</p>
              <p className="text-xs mt-1 text-muted-foreground/80">Tag issue movements as “Spoilage/Waste” or “Return to Vendor” to populate this report.</p>
            </div>
          ) : (
            <div className="border rounded-lg overflow-x-auto">
              <table className="w-full min-w-[720px] text-sm">
                <thead className="bg-red-50/50">
                  <tr>
                    <WasteTh align="left" help={ReportHelp.COL_WASTE_DATE}>Date</WasteTh>
                    <WasteTh align="left" help={ReportHelp.COL_WASTE_ITEM}>Item</WasteTh>
                    <WasteTh align="left" help={ReportHelp.COL_CATEGORY}>Category</WasteTh>
                    <WasteTh align="right" help={ReportHelp.COL_WASTE_QTY}>Qty wasted</WasteTh>
                    <WasteTh align="left" help={ReportHelp.COL_WASTE_REASON}>Reason</WasteTh>
                    <WasteTh align="right" help={ReportHelp.COL_WASTE_VALUE}>Lost value</WasteTh>
                    <WasteTh align="center" help="Traffic-light severity: > 50 lost value = alert, > 15 = watch, otherwise healthy.">Severity</WasteTh>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {wasteData.map((w, i) => {
                    const severity: 'green' | 'yellow' | 'red' = w.lost_value > 50 ? 'red' : w.lost_value > 15 ? 'yellow' : 'green'
                    return (
                      <tr key={i} className={i % 2 === 1 ? 'bg-muted/20 hover:bg-muted/40' : 'hover:bg-muted/30'}>
                        <td className="px-4 py-3 text-sm text-muted-foreground whitespace-nowrap">{w.date}</td>
                        <td className="px-4 py-3 text-sm font-medium">{w.item_name}</td>
                        <td className="px-4 py-3 text-sm text-muted-foreground">{w.category}</td>
                        <td className="px-4 py-3 text-right text-sm tabular-nums text-red-600 font-medium">{w.qty_wasted.toFixed(1)} {w.unit}</td>
                        <td className="px-4 py-3 text-sm">
                          <Badge variant="outline" className="text-xs font-normal">{w.reason}</Badge>
                        </td>
                        <td className="px-4 py-3 text-right text-sm tabular-nums font-semibold text-red-600">{formatCurrency(w.lost_value)}</td>
                        <td className="px-4 py-3 text-center"><TrafficLight status={severity} /></td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* ── Consumption Variance (physical counts) ────────────── */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
            <div className="space-y-1">
              <CardTitle className="text-base font-semibold">Consumption variance (physical counts)</CardTitle>
              <CardDescription className="text-xs max-w-2xl">
                Variance is your physical count minus the system (book) quantity at count time. Only items counted in {periodLabel.toLowerCase()} appear — record a count from an item’s menu (“Record count”) to populate this.
              </CardDescription>
            </div>
            {countedRows.length > 0 && (
              <Badge variant="outline" className="text-xs whitespace-nowrap self-start">
                {countedRows.length} counted{uncountedCount > 0 ? ` · ${uncountedCount} not counted` : ''}
              </Badge>
            )}
          </div>
        </CardHeader>
        <CardContent className="pt-0">
          {countedRows.length === 0 ? (
            <div className="text-center py-10 text-muted-foreground">
              <ClipboardCheck className="w-8 h-8 mx-auto mb-2 opacity-40" />
              <p className="text-sm font-medium">No physical counts recorded in this period.</p>
              <p className="text-xs mt-1 text-muted-foreground/80">Use “Record count” on an item (Inventory tab) to capture a count and surface shrinkage here.</p>
            </div>
          ) : (
            <div className="border rounded-lg overflow-x-auto">
              <table className="w-full min-w-[720px] text-sm">
                <thead className="bg-muted/50">
                  <tr>
                    <th className="px-4 py-3 text-left font-semibold">Item</th>
                    <th className="px-4 py-3 text-left font-semibold">Category</th>
                    <th className="px-4 py-3 text-right font-semibold">Counted</th>
                    <th className="px-4 py-3 text-right font-semibold">Book</th>
                    <th className="px-4 py-3 text-right font-semibold">Variance</th>
                    <th className="px-4 py-3 text-right font-semibold">Value impact</th>
                    <th className="px-4 py-3 text-left font-semibold">Counted on</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {countedRows.map((v, i) => {
                    const variance = v.variance ?? 0
                    const tone = Math.abs(variance) <= 1e-9 ? 'text-green-700' : variance < 0 ? 'text-red-600' : 'text-amber-700'
                    const bookAtCount = (v.counted ?? 0) - variance
                    return (
                      <tr key={v.item_id || i} className={i % 2 === 1 ? 'bg-muted/20 hover:bg-muted/40' : 'hover:bg-muted/30'}>
                        <td className="px-4 py-3 text-sm font-medium">{v.item_name}</td>
                        <td className="px-4 py-3 text-sm text-muted-foreground">{v.category}</td>
                        <td className="px-4 py-3 text-right text-sm tabular-nums">{(v.counted ?? 0)} {v.unit}</td>
                        <td className="px-4 py-3 text-right text-sm tabular-nums text-muted-foreground">{+bookAtCount.toFixed(4)} {v.unit}</td>
                        <td className={`px-4 py-3 text-right text-sm tabular-nums font-semibold ${tone}`}>{variance > 0 ? '+' : ''}{+variance.toFixed(4)} {v.unit}</td>
                        <td className={`px-4 py-3 text-right text-sm tabular-nums ${tone}`}>{variance >= 0 ? '' : '-'}{formatCurrency(Math.abs(variance) * v.unit_cost)}</td>
                        <td className="px-4 py-3 text-sm text-muted-foreground whitespace-nowrap">{v.counted_at ?? '—'}</td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}

function KpiCard({ icon, tint, label, value, valueTone = '', cardClass = '', hint, footer }: {
  icon: React.ReactNode
  tint: string
  label: string
  value: string
  valueTone?: string
  cardClass?: string
  hint: string
  footer?: string
}) {
  return (
    <Card className={cardClass}>
      <CardContent className="pt-5 pb-4 px-5">
        <div className="flex items-start gap-3">
          <div className={`p-2.5 rounded-xl ${tint} shrink-0`}>{icon}</div>
          <div className="min-w-0 flex-1">
            <p className={`text-2xl font-bold truncate ${valueTone}`}>{value}</p>
            <div className="flex items-center gap-1.5 mt-0.5">
              <p className="text-xs text-muted-foreground truncate">{label}</p>
              <MetricHint text={hint} label={label} />
            </div>
            {footer && <p className="text-[11px] text-muted-foreground/80 mt-1 truncate">{footer}</p>}
          </div>
        </div>
      </CardContent>
    </Card>
  )
}

function WasteTh({ children, align, help }: {
  children: React.ReactNode; align: 'left' | 'right' | 'center'; help: string
}) {
  const alignCls = align === 'left' ? 'text-left' : align === 'right' ? 'text-right' : 'text-center'
  const flexJustify = align === 'left' ? 'justify-start' : align === 'right' ? 'justify-end' : 'justify-center'
  return (
    <th className={`${alignCls} px-4 py-3 font-medium text-sm`}>
      <span className={`inline-flex items-center gap-1.5 ${flexJustify}`}>
        {children}
        <MetricHint text={help} label={String(children)} />
      </span>
    </th>
  )
}

function TrafficLight({ status }: { status: 'green' | 'yellow' | 'red' }) {
  const colors = {
    green: 'bg-green-500',
    yellow: 'bg-amber-400',
    red: 'bg-red-500',
  }
  const labels = {
    green: 'Healthy',
    yellow: 'Watch',
    red: 'Alert',
  }
  return (
    <span className="inline-flex items-center gap-1.5">
      <span className={`w-2 h-2 rounded-full ${colors[status]}`} />
      <span className={`text-[10px] font-medium ${status === 'red' ? 'text-red-600' : status === 'yellow' ? 'text-amber-600' : 'text-green-600'}`}>
        {labels[status]}
      </span>
    </span>
  )
}

// ─── Shared components ───────────────────────────────────────────

function Pagination({ page, totalPages, setPage }: { page: number; totalPages: number; setPage: (p: number) => void }) {
  return (
    <div className="flex items-center justify-between">
      <p className="text-sm text-muted-foreground">Page {page} of {totalPages}</p>
      <div className="flex gap-1">
        <Button size="sm" variant="outline" disabled={page <= 1} onClick={() => setPage(page - 1)}><ChevronLeft className="w-4 h-4" /></Button>
        <Button size="sm" variant="outline" disabled={page >= totalPages} onClick={() => setPage(page + 1)}><ChevronRight className="w-4 h-4" /></Button>
      </div>
    </div>
  )
}

function ModalOverlay({ children, onClose }: { children: React.ReactNode; onClose: () => void }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={onClose}>
      <div className="bg-background rounded-lg shadow-xl w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        {children}
      </div>
    </div>
  )
}

// ─── Form components ─────────────────────────────────────────────

function EditItemForm({ item, categories, onClose, qc, showToast }: { item: StockItem; categories: StockCategory[]; onClose: () => void; qc: any; showToast: (t: 'success' | 'error', m: string) => void }) {
  const { currencyCode } = useCurrency()
  const [form, setForm] = useState({ name: item.name, unit: item.unit, category_id: item.category_id ?? '', reorder_level: item.reorder_level, default_unit_cost: item.default_unit_cost ?? 0, supplier_id: item.supplier_id ?? '', notes: item.notes ?? '' })
  const isWeight = UNIT_OPTIONS.weight.some(o => o.value === item.unit)
  const [unitMode, setUnitMode] = useState<UnitMode>(isWeight ? 'weight' : 'quantity')
  const { data: suppliersRes } = useQuery({ queryKey: ['suppliers'], queryFn: () => apiClient.listSuppliers() })
  const suppliers = suppliersRes?.data ?? []
  const mut = useMutation({
    mutationFn: () => apiClient.updateStockItem(item.id, {
      name: form.name, unit: form.unit,
      category_id: form.category_id || undefined,
      reorder_level: form.reorder_level,
      default_unit_cost: form.default_unit_cost || undefined,
      // Send '' to explicitly clear the vendor; a UUID to set it.
      supplier_id: form.supplier_id,
      notes: form.notes || undefined,
    } as any),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['stockItems'] })
      qc.invalidateQueries({ queryKey: ['stockSummary'] })
      qc.invalidateQueries({ queryKey: ['advancedStockReport'] })
      showToast('success', 'Item updated')
      onClose()
    },
    onError: (err: Error) => showToast('error', err.message || 'Failed to update item'),
  })

  return (
    <form onSubmit={e => { e.preventDefault(); mut.mutate() }} className="p-6 space-y-4">
      <div className="flex items-center justify-between mb-2">
        <h2 className="text-lg font-semibold">Edit: {item.name}</h2>
        <button type="button" onClick={onClose}><X className="w-5 h-5" /></button>
      </div>
      <FormField label="Name" required><input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} required /></FormField>
      <div className="grid grid-cols-2 gap-4">
        <FormField label="Measured By">
          <UnitSelector unitMode={unitMode} unit={form.unit} onModeChange={mode => { setUnitMode(mode); setForm(f => ({ ...f, unit: UNIT_OPTIONS[mode][0].value })) }} onUnitChange={u => setForm(f => ({ ...f, unit: u }))} />
        </FormField>
        <FormField label="Category">
          <select value={form.category_id} onChange={e => setForm(f => ({ ...f, category_id: e.target.value }))}>
            <option value="">None</option>
            {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </FormField>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <FormField label="Reorder Level"><input type="number" step="any" value={form.reorder_level} onChange={e => setForm(f => ({ ...f, reorder_level: +e.target.value }))} /></FormField>
        <FormField label={`Unit Cost (${currencyCode})`}><input type="number" step="0.01" value={form.default_unit_cost} onChange={e => setForm(f => ({ ...f, default_unit_cost: +e.target.value }))} /></FormField>
      </div>
      <FormField label="Vendor / supplier">
        <select value={form.supplier_id} onChange={e => setForm(f => ({ ...f, supplier_id: e.target.value }))}>
          <option value="">None</option>
          {suppliers.map(s => (
            <option key={s.id} value={s.id}>
              {s.name}{s.commission_percent ? ` (${s.commission_percent}% commission)` : ''}
            </option>
          ))}
        </select>
      </FormField>
      <FormField label="Notes"><textarea rows={2} value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} /></FormField>
      <Button type="submit" className="w-full" disabled={mut.isPending || !form.name}>{mut.isPending ? 'Saving...' : 'Save Changes'}</Button>
    </form>
  )
}

function PurchaseForm({ item, onClose, qc, showToast }: { item: StockItem; onClose: () => void; qc: any; showToast: (t: 'success' | 'error', m: string) => void }) {
  const { formatCurrency, currencyCode } = useCurrency()
  const [form, setForm] = useState({ quantity: 0, unit_cost: item.default_unit_cost ?? 0, note: '', expiry_date: '', post_to_expense: false })
  const mut = useMutation({
    mutationFn: () =>
      apiClient.purchaseStock(item.id, {
        quantity: form.quantity,
        unit_cost: form.unit_cost || undefined,
        note: form.note || undefined,
        expiry_date: form.expiry_date.trim() || undefined,
        post_to_expense: form.post_to_expense,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['stockItems'] })
      qc.invalidateQueries({ queryKey: ['stockAlerts'] })
      qc.invalidateQueries({ queryKey: ['stockMovements'] })
      qc.invalidateQueries({ queryKey: ['stockSummary'] })
      qc.invalidateQueries({ queryKey: ['advancedStockReport'] })
      showToast('success', `Purchased ${form.quantity} ${item.unit} of ${item.name}`)
      onClose()
    },
    onError: (err: Error) => showToast('error', err.message || 'Failed to record purchase'),
  })

  return (
    <form onSubmit={e => { e.preventDefault(); mut.mutate() }} className="p-6 space-y-4">
      <div className="flex items-center justify-between mb-2">
        <h2 className="text-lg font-semibold">Purchase: {item.name}</h2>
        <button type="button" onClick={onClose}><X className="w-5 h-5" /></button>
      </div>
      <p className="text-sm text-muted-foreground">Current stock: {item.quantity_on_hand} {item.unit}</p>
      <div className="grid grid-cols-2 gap-4">
        <FormField label={`Quantity (${item.unit})`} required><input type="number" step="any" min="0.01" value={form.quantity || ''} onChange={e => setForm(f => ({ ...f, quantity: +e.target.value }))} required /></FormField>
        <FormField label={`Unit Cost (${currencyCode})`}><input type="number" step="0.01" value={form.unit_cost || ''} onChange={e => setForm(f => ({ ...f, unit_cost: +e.target.value }))} /></FormField>
      </div>
      {form.quantity > 0 && form.unit_cost > 0 && <p className="text-sm font-medium">Total cost: {formatCurrency(form.quantity * form.unit_cost)}</p>}
      <FormField label="Expiry (optional)">
        <DatePicker value={form.expiry_date} onChange={v => setForm(f => ({ ...f, expiry_date: v }))} />
      </FormField>
      <FormField label="Note"><input value={form.note} onChange={e => setForm(f => ({ ...f, note: e.target.value }))} placeholder="e.g. Weekly supplier order" /></FormField>
      <label className="flex items-start gap-2 rounded-md border bg-muted/30 p-3 text-sm cursor-pointer">
        <input type="checkbox" className="mt-0.5 h-4 w-4" checked={form.post_to_expense} onChange={e => setForm(f => ({ ...f, post_to_expense: e.target.checked }))} />
        <span>
          <span className="font-medium">Add to expenses now</span> — books the purchase value as an expense today (cash-basis).
          Leave unchecked to treat it as an asset that becomes an expense (COGS) as it&apos;s sold, avoiding double-counting.
        </span>
      </label>
      <Button type="submit" className="w-full" disabled={mut.isPending || form.quantity <= 0}>{mut.isPending ? 'Recording...' : 'Record Purchase'}</Button>
    </form>
  )
}

// ─── Redesigned Issue Form with unit conversion + reason ─────────

function IssueForm({ item, users, onClose, qc, showToast }: { item: StockItem; users: UserBrief[]; onClose: () => void; qc: any; showToast: (t: 'success' | 'error', m: string) => void }) {
  const compatibleUnits = getCompatibleUnits(item.unit)
  const hasUnitOptions = compatibleUnits.length > 1

  const [form, setForm] = useState({
    quantity: 0,
    unit: item.unit,
    issued_to_user_id: '',
    reason: 'General Kitchen Use',
    note: '',
  })

  const convertedQty = form.unit === item.unit
    ? form.quantity
    : convertUnits(form.quantity, form.unit, item.unit)

  const conversionError = form.quantity > 0 && convertedQty === null
  const insufficientStock = convertedQty != null && convertedQty > item.quantity_on_hand

  const issuedUser = users.find((u: UserBrief) => u.id === form.issued_to_user_id)
  const mut = useMutation({
    mutationFn: () => apiClient.issueStock(item.id, {
      quantity: form.quantity,
      unit: form.unit !== item.unit ? form.unit : undefined,
      issued_to_user_id: form.issued_to_user_id,
      reason: form.reason || undefined,
      note: form.note || undefined,
    }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['stockItems'] })
      qc.invalidateQueries({ queryKey: ['stockAlerts'] })
      qc.invalidateQueries({ queryKey: ['stockMovements'] })
      qc.invalidateQueries({ queryKey: ['stockSummary'] })
      qc.invalidateQueries({ queryKey: ['advancedStockReport'] })
      showToast('success', `Issued ${form.quantity} ${form.unit} of ${item.name} to ${issuedUser?.first_name ?? 'staff'}`)
      onClose()
    },
    onError: (err: Error) => showToast('error', err.message || 'Failed to issue stock'),
  })

  const canSubmit = form.quantity > 0 && !!form.issued_to_user_id && !conversionError && !insufficientStock && !mut.isPending

  return (
    <form onSubmit={e => { e.preventDefault(); mut.mutate() }} className="p-6 space-y-4">
      <div className="flex items-center justify-between mb-2">
        <h2 className="text-lg font-semibold">Issue: {item.name}</h2>
        <button type="button" onClick={onClose}><X className="w-5 h-5" /></button>
      </div>

      <div className="flex items-center gap-2 text-sm bg-muted/50 rounded-lg px-3 py-2">
        <Package className="w-4 h-4 text-muted-foreground" />
        <span className="text-muted-foreground">Available:</span>
        <span className="font-semibold">{item.quantity_on_hand} {item.unit}</span>
      </div>

      {/* Quantity + Unit side by side */}
      <div className={`grid gap-4 ${hasUnitOptions ? 'grid-cols-2' : 'grid-cols-1'}`}>
        <FormField label="Quantity" required>
          <input type="number" step="any" min="0.01" value={form.quantity || ''} onChange={e => setForm(f => ({ ...f, quantity: +e.target.value }))} required />
        </FormField>
        {hasUnitOptions && (
          <FormField label="Unit">
            <select value={form.unit} onChange={e => setForm(f => ({ ...f, unit: e.target.value }))}>
              {compatibleUnits.map(u => {
                const allUnits = [...UNIT_OPTIONS.weight, ...UNIT_OPTIONS.quantity]
                const found = allUnits.find(o => o.value === u)
                return <option key={u} value={u}>{found?.label ?? u}</option>
              })}
            </select>
          </FormField>
        )}
      </div>

      {/* Conversion preview */}
      {form.quantity > 0 && form.unit !== item.unit && (
        <div className={`text-sm rounded-lg px-3 py-2 ${conversionError ? 'bg-red-50 text-red-700' : insufficientStock ? 'bg-amber-50 text-amber-700' : 'bg-blue-50 text-blue-700'}`}>
          {conversionError && 'Cannot convert between these units'}
          {!conversionError && convertedQty != null && (
            <>
              {form.quantity} {form.unit} = <strong>{convertedQty.toFixed(4)} {item.unit}</strong> will be deducted
              {insufficientStock && <span className="block mt-0.5 font-medium">Exceeds available stock ({item.quantity_on_hand} {item.unit})</span>}
            </>
          )}
        </div>
      )}

      {insufficientStock && form.unit === item.unit && form.quantity > 0 && (
        <div className="text-sm rounded-lg px-3 py-2 bg-amber-50 text-amber-700">
          Requested {form.quantity} {item.unit} exceeds available stock ({item.quantity_on_hand} {item.unit})
        </div>
      )}

      <FormField label="Reason" required>
        <select value={form.reason} onChange={e => setForm(f => ({ ...f, reason: e.target.value }))} required>
          {ISSUE_REASONS.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
        </select>
      </FormField>

      <FormField label="Issue To" required>
        <select value={form.issued_to_user_id} onChange={e => setForm(f => ({ ...f, issued_to_user_id: e.target.value }))} required>
          <option value="">Select person...</option>
          {users.map(u => <option key={u.id} value={u.id}>{u.first_name} {u.last_name} ({u.role})</option>)}
        </select>
      </FormField>

      <FormField label="Note (optional)"><input value={form.note} onChange={e => setForm(f => ({ ...f, note: e.target.value }))} placeholder="e.g. For kitchen prep" /></FormField>

      <Button type="submit" className="w-full" disabled={!canSubmit}>
        {mut.isPending ? 'Issuing...' : `Issue ${form.quantity > 0 ? form.quantity + ' ' + form.unit : 'Stock'}`}
      </Button>
    </form>
  )
}

function ReturnToSupplierForm({ item, onClose, qc, showToast }: { item: StockItem; onClose: () => void; qc: any; showToast: (t: 'success' | 'error', m: string) => void }) {
  const { formatCurrency } = useCurrency()
  const { data: suppliersRes } = useQuery({ queryKey: ['suppliers'], queryFn: () => apiClient.listSuppliers() })
  const suppliers = suppliersRes?.data ?? []
  const [form, setForm] = useState({
    supplier_id: '',
    quantity: '',
    refund_type: 'supplier_credit' as 'cash' | 'bank' | 'supplier_credit' | 'no_refund',
    refund_amount: '',
    reason: '',
    pin: '',
  })

  const qty = Number(form.quantity)
  const refundAmount = form.refund_type === 'no_refund' ? 0 : Number(form.refund_amount || 0)
  const qtyValid = Number.isFinite(qty) && qty > 0
  const tooMuch = qtyValid && qty > item.quantity_on_hand + 1e-9
  const refundInvalid = form.refund_type !== 'no_refund' && (!Number.isFinite(refundAmount) || refundAmount < 0)

  const mut = useMutation({
    mutationFn: () =>
      apiClient.returnStockToSupplier(item.id, {
        supplier_id: form.supplier_id,
        quantity: qty,
        unit_cost: item.default_unit_cost ?? undefined,
        refund_amount: refundAmount,
        refund_type: form.refund_type,
        reason: form.reason.trim(),
        pin: form.pin.trim(),
      }),
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: ['stockItems'] })
      qc.invalidateQueries({ queryKey: ['stockAlerts'] })
      qc.invalidateQueries({ queryKey: ['stockMovements'] })
      qc.invalidateQueries({ queryKey: ['stockSummary'] })
      qc.invalidateQueries({ queryKey: ['advancedStockReport'] })
      showToast('success', res.message || 'Supplier return recorded')
      onClose()
    },
    onError: (err: Error) => showToast('error', err.message || 'Failed to record supplier return'),
  })

  const canSubmit =
    !!form.supplier_id &&
    qtyValid &&
    !tooMuch &&
    !refundInvalid &&
    form.reason.trim().length > 0 &&
    form.pin.length === 4 &&
    !mut.isPending

  return (
    <form onSubmit={e => { e.preventDefault(); if (canSubmit) mut.mutate() }} className="p-6 space-y-4">
      <div className="flex items-center justify-between mb-2">
        <div>
          <h2 className="text-lg font-semibold">Return to supplier</h2>
          <p className="text-sm text-muted-foreground">{item.name}</p>
        </div>
        <button type="button" onClick={onClose}><X className="w-5 h-5" /></button>
      </div>

      <div className="rounded-lg border bg-muted/40 p-3 text-sm">
        <div className="flex justify-between gap-3">
          <span className="text-muted-foreground">On hand</span>
          <span className="font-semibold">{item.quantity_on_hand} {item.unit}</span>
        </div>
        <div className="flex justify-between gap-3 mt-1">
          <span className="text-muted-foreground">Default unit cost</span>
          <span className="font-semibold">{item.default_unit_cost != null ? formatCurrency(item.default_unit_cost) : '—'}</span>
        </div>
      </div>

      <FormField label="Supplier" required>
        <select value={form.supplier_id} onChange={e => setForm(f => ({ ...f, supplier_id: e.target.value }))} required>
          <option value="">Select supplier...</option>
          {suppliers.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
        </select>
      </FormField>

      <FormField label={`Quantity to return (${item.unit})`} required>
        <input
          type="number"
          step="0.01"
          min="0"
          value={form.quantity}
          onChange={e => setForm(f => ({ ...f, quantity: e.target.value }))}
          placeholder="0.00"
          required
        />
      </FormField>
      {tooMuch && (
        <p className="text-sm text-amber-700 bg-amber-50 rounded-md px-3 py-2">
          Cannot return more than on-hand stock ({item.quantity_on_hand} {item.unit}).
        </p>
      )}

      <div className="grid grid-cols-2 gap-3">
        <FormField label="Refund type" required>
          <select
            value={form.refund_type}
            onChange={e => {
              const next = e.target.value as typeof form.refund_type
              setForm(f => ({ ...f, refund_type: next, refund_amount: next === 'no_refund' ? '' : f.refund_amount }))
            }}
            required
          >
            <option value="supplier_credit">Supplier credit</option>
            <option value="cash">Cash refund</option>
            <option value="bank">Bank refund</option>
            <option value="no_refund">No refund</option>
          </select>
        </FormField>
        <FormField label="Refund amount">
          <input
            type="number"
            step="0.01"
            min="0"
            value={form.refund_amount}
            disabled={form.refund_type === 'no_refund'}
            onChange={e => setForm(f => ({ ...f, refund_amount: e.target.value }))}
            placeholder={form.refund_type === 'no_refund' ? 'N/A' : '0.00'}
          />
        </FormField>
      </div>

      <FormField label="Reason" required>
        <input
          value={form.reason}
          onChange={e => setForm(f => ({ ...f, reason: e.target.value }))}
          placeholder="e.g. Expired before use; supplier agreed to credit"
          required
        />
      </FormField>

      <FormField label="Admin / manager PIN" required>
        <input
          type="password"
          inputMode="numeric"
          maxLength={4}
          value={form.pin}
          onChange={e => setForm(f => ({ ...f, pin: e.target.value.replace(/\D/g, '').slice(0, 4) }))}
          required
        />
      </FormField>

      <Button type="submit" className="w-full" disabled={!canSubmit}>
        {mut.isPending ? 'Recording...' : 'Record supplier return'}
      </Button>
    </form>
  )
}

type AdjustReason = typeof ADJUST_REASONS[number]['value']
type AdjustFormState = {
  reason: AdjustReason
  unit_cost: number
  note: string
}

function AdjustForm({ item, onClose, qc, showToast }: { item: StockItem; onClose: () => void; qc: any; showToast: (t: 'success' | 'error', m: string) => void }) {
  const { formatCurrency, currencyCode } = useCurrency()
  const [qtyStr, setQtyStr] = useState('')
  const [form, setForm] = useState<AdjustFormState>({
    reason: ADJUST_REASONS[0].value,
    unit_cost: item.default_unit_cost ?? 0,
    note: '',
  })

  const delta = qtyStr.trim() === '' ? NaN : Number(qtyStr)
  const deltaValid = Number.isFinite(delta) && Math.abs(delta) > 1e-9
  const tooMuchRemoval = deltaValid && delta < 0 && Math.abs(delta) > item.quantity_on_hand + 1e-9

  const mut = useMutation({
    mutationFn: () =>
      apiClient.adjustStock(item.id, {
        quantity_delta: delta,
        reason: form.reason || undefined,
        note: form.note.trim() || undefined,
        ...(delta > 0 && form.unit_cost > 0 ? { unit_cost: form.unit_cost } : {}),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['stockItems'] })
      qc.invalidateQueries({ queryKey: ['stockAlerts'] })
      qc.invalidateQueries({ queryKey: ['stockMovements'] })
      qc.invalidateQueries({ queryKey: ['stockSummary'] })
      qc.invalidateQueries({ queryKey: ['advancedStockReport'] })
      showToast('success', `Adjusted ${item.name} by ${delta > 0 ? '+' : ''}${delta} ${item.unit}`)
      onClose()
    },
    onError: (err: Error) => showToast('error', err.message || 'Failed to adjust stock'),
  })

  const canSubmit = deltaValid && !tooMuchRemoval && !mut.isPending
  const qtyParseError = qtyStr.trim() !== '' && !Number.isFinite(delta)

  return (
    <form onSubmit={e => { e.preventDefault(); if (!deltaValid || tooMuchRemoval) return; mut.mutate() }} className="p-6 space-y-4">
      <div className="flex items-center justify-between mb-2">
        <h2 className="text-lg font-semibold">Adjust: {item.name}</h2>
        <button type="button" onClick={onClose}><X className="w-5 h-5" /></button>
      </div>
      <p className="text-sm text-muted-foreground">
        Current on hand: <span className="font-medium text-foreground">{item.quantity_on_hand} {item.unit}</span>.
        Enter a positive number to add stock, or a negative number to remove it. When you remove stock, older receipts are used first so costs stay fair.
      </p>
      <FormField label={`Quantity change (${item.unit})`} required>
        <input
          type="text"
          inputMode="decimal"
          value={qtyStr}
          onChange={e => setQtyStr(e.target.value)}
          placeholder="e.g. 2 or -1.5"
          required
        />
      </FormField>
      {qtyParseError && (
        <p className="text-sm text-red-600">Enter a valid number.</p>
      )}
      {tooMuchRemoval && (
        <p className="text-sm text-amber-700 bg-amber-50 rounded-md px-3 py-2">
          Cannot remove more than on hand ({item.quantity_on_hand} {item.unit}).
        </p>
      )}
      {delta > 0 && (
        <FormField label={`Unit cost for new lot (${currencyCode}, optional)`}>
          <input type="number" step="0.01" min="0" value={form.unit_cost || ''} onChange={e => setForm(f => ({ ...f, unit_cost: +e.target.value }))} />
        </FormField>
      )}
      {delta > 0 && form.unit_cost > 0 && (
        <p className="text-xs text-muted-foreground">Lot value: {formatCurrency(delta * form.unit_cost)}</p>
      )}
      <FormField label="Reason" required>
        <select value={form.reason} onChange={e => setForm(f => ({ ...f, reason: e.target.value as AdjustReason }))} required>
          {ADJUST_REASONS.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
        </select>
      </FormField>
      <FormField label="Note (optional)">
        <input value={form.note} onChange={e => setForm(f => ({ ...f, note: e.target.value }))} placeholder="e.g. Year-end count" />
      </FormField>
      <Button type="submit" className="w-full" disabled={!canSubmit}>
        {mut.isPending ? 'Saving...' : 'Apply adjustment'}
      </Button>
    </form>
  )
}

function RecordCountForm({ item, onClose, qc, showToast }: { item: StockItem; onClose: () => void; qc: any; showToast: (t: 'success' | 'error', m: string) => void }) {
  const [countedStr, setCountedStr] = useState('')
  const [note, setNote] = useState('')

  const counted = countedStr.trim() === '' ? NaN : Number(countedStr)
  const countedValid = Number.isFinite(counted) && counted >= 0
  const parseError = countedStr.trim() !== '' && (!Number.isFinite(counted) || counted < 0)
  const variance = countedValid ? counted - item.quantity_on_hand : NaN

  const mut = useMutation({
    mutationFn: () =>
      apiClient.recordStockCount(item.id, {
        counted_quantity: counted,
        note: note.trim() || undefined,
      }),
    onSuccess: () => {
      // Counts are non-destructive (no stock moves), so only the variance
      // report and the activity log change — refresh those.
      qc.invalidateQueries({ queryKey: ['advancedStockReport'] })
      qc.invalidateQueries({ queryKey: ['inventoryActivity'] })
      const v = counted - item.quantity_on_hand
      showToast('success', `Counted ${item.name}: ${counted} ${item.unit} (variance ${v > 0 ? '+' : ''}${+v.toFixed(4)} ${item.unit})`)
      onClose()
    },
    onError: (err: Error) => showToast('error', err.message || 'Failed to record count'),
  })

  const canSubmit = countedValid && !mut.isPending

  return (
    <form onSubmit={e => { e.preventDefault(); if (!countedValid) return; mut.mutate() }} className="p-6 space-y-4">
      <div className="flex items-center justify-between mb-2">
        <h2 className="text-lg font-semibold">Record count: {item.name}</h2>
        <button type="button" onClick={onClose}><X className="w-5 h-5" /></button>
      </div>
      <p className="text-sm text-muted-foreground">
        System (book) on hand: <span className="font-medium text-foreground tabular-nums">{item.quantity_on_hand} {item.unit}</span>.
        Enter the quantity you physically counted. This records the variance for the report — it does <span className="font-medium text-foreground">not</span> change stock. To correct the book quantity, use Adjust afterwards.
      </p>
      <FormField label={`Counted quantity (${item.unit})`} required>
        <input
          type="text"
          inputMode="decimal"
          value={countedStr}
          onChange={e => setCountedStr(e.target.value)}
          placeholder="e.g. 12 or 4.5"
          required
        />
      </FormField>
      {parseError && (
        <p className="text-sm text-red-600">Enter a valid non-negative number.</p>
      )}
      {countedValid && Math.abs(variance) > 1e-9 && (
        <p className={`text-sm rounded-md px-3 py-2 ${variance < 0 ? 'bg-red-50 text-red-700' : 'bg-amber-50 text-amber-700'}`}>
          Variance: <span className="font-semibold tabular-nums">{variance > 0 ? '+' : ''}{+variance.toFixed(4)} {item.unit}</span>
          {' '}({variance < 0 ? 'shortage vs book' : 'overage vs book'}).
        </p>
      )}
      {countedValid && Math.abs(variance) <= 1e-9 && (
        <p className="text-sm rounded-md px-3 py-2 bg-green-50 text-green-700">
          Matches book exactly — no variance.
        </p>
      )}
      <FormField label="Note (optional)">
        <input value={note} onChange={e => setNote(e.target.value)} placeholder="e.g. Monthly stock-take" />
      </FormField>
      <Button type="submit" className="w-full" disabled={!canSubmit}>
        {mut.isPending ? 'Recording...' : 'Record count'}
      </Button>
    </form>
  )
}
